import type { IChannelPayloadPlaylistAssignment } from '@lucimark/shared/models/player-registration-payload'
import { playlistUrnIsPlayerMasterPlaylist } from '@lucimark/shared/helpers/playerMasterPlaylist'

export interface TOrganizationDistributionList {
  id: string
  name: string
  superseedsPlayerMaster?: boolean
}

export type TOrganizationContentPlaylistRow = Pick<IChannelPayloadPlaylistAssignment, 'index' | 'playlistUrn' | 'title'> & {
  playlist?: { title?: string }
}

/** Status overlay: membership line when the screen belongs to distribution lists. */
export function formatDistributionListsSummary(
  lists: ReadonlyArray<TOrganizationDistributionList> | null | undefined
): string | null {
  if (lists == null || lists.length === 0) {
    return null
  }
  const parts = lists.map((l) => {
    const name = l.name.trim() !== '' ? l.name.trim() : '—'
    return l.superseedsPlayerMaster === true ? `${name} (subst.)` : name
  })
  return parts.join(' · ')
}

function playlistRowDisplayTitle(p: TOrganizationContentPlaylistRow): string {
  const nested = p.playlist?.title?.trim()
  if (nested) {
    return nested
  }
  const top = typeof p.title === 'string' ? p.title.trim() : ''
  return top !== '' ? top : '—'
}

/**
 * Editorial playlists on the active combined schedule root, excluding auto-managed
 * per-screen masters (`player-master-*`). Used as “Conteúdo” when distribution lists drive playback.
 */
export function formatEditorialContentSummary(
  playlists: ReadonlyArray<TOrganizationContentPlaylistRow> | null | undefined
): string | null {
  if (playlists == null || playlists.length === 0) {
    return null
  }
  const editorial = [...playlists]
    .filter(p => !playlistUrnIsPlayerMasterPlaylist(p.playlistUrn))
    .sort((a, b) => a.index - b.index)
  const titles = editorial
    .map(p => playlistRowDisplayTitle(p))
    .filter(t => t !== '—')
  if (titles.length === 0) {
    return null
  }
  // Dedupe consecutive identical titles (master+list reuse of the same editorial URN).
  const deduped: string[] = []
  for (const t of titles) {
    if (deduped[deduped.length - 1] !== t) {
      deduped.push(t)
    }
  }
  return deduped.join(' · ')
}

/**
 * Legacy “Feed” line when the screen has no distribution lists: campaign title + version,
 * or joined playlist titles when the combined root has multiple slots.
 */
export function formatFeedScheduleSummary(input: {
  playlists: ReadonlyArray<TOrganizationContentPlaylistRow> | null | undefined
  rootTitle?: string | null
  rootVersion?: number | null
}): string {
  const pls = [...(input.playlists ?? [])].sort((a, b) => a.index - b.index)
  if (pls.length > 1) {
    const titles = pls.map(p => playlistRowDisplayTitle(p)).filter(t => t !== '—')
    if (titles.length > 0) {
      return titles.join(' · ')
    }
  }
  if (pls.length === 1) {
    const title = playlistRowDisplayTitle(pls[0]!)
    if (title !== '—') {
      const ver = input.rootVersion
      return ver != null && Number.isFinite(ver) ? `${title} v${ver}` : title
    }
  }
  const rootTitle = typeof input.rootTitle === 'string' ? input.rootTitle.trim() : ''
  const rootVer = input.rootVersion
  if (rootTitle !== '' && rootVer != null && Number.isFinite(rootVer)) {
    return `${rootTitle} v${rootVer}`
  }
  if (rootTitle !== '') {
    return rootTitle
  }
  return '—'
}
